/**
 * Wiki / Knowledge Base API — articles CRUD, search.
 */

import { apiFetch } from './core'

export const wiki = {
    articles: (params = {}) => {
        // Защита от агрессивного браузерного (и SW) кэширования GET-запросов
        params._t = Date.now()
        const qs = new URLSearchParams(params).toString()
        return apiFetch(`/wiki/${qs ? `?${qs}` : ''}`)
    },
    // ``with_progress=true`` хидрирует my_pending_count + my_last_reviewer_*
    // в ответ, чтобы ArticleViewer отрисовал статус-блок сотрудника без
    // второго round-trip. Безопасно для анонимных запросов — backend
    // молча скипает hydrate если current_user/employee_id отсутствует.
    getArticle: (id) => apiFetch(`/wiki/${id}?with_progress=true`),
    createArticle: (data) => apiFetch('/wiki/', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateArticle: (id, data) => apiFetch(`/wiki/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    // ── Cloudflare Stream (защищённое видео в статьях) ──
    // Создаёт одноразовый resumable-upload (браузер льёт файл прямо в Cloudflare).
    createStreamUpload: (filename, filesize) => apiFetch('/wiki/stream/upload-url', {
        method: 'POST',
        body: JSON.stringify({ filename, filesize }),
    }),
    // Короткоживущий signed-token на просмотр защищённого ролика.
    getStreamToken: (uid) => apiFetch(`/wiki/stream/${encodeURIComponent(uid)}/token`),
    restoreVersion: (id, versionNumber, expectedCurrentVersion = null) =>
        apiFetch(`/wiki/${id}/restore-version`, {
            method: 'POST',
            body: JSON.stringify({
                version_number: versionNumber,
                expected_current_version: expectedCurrentVersion,
                comment: `Restore from v${versionNumber}`,
            }),
        }),
    restoreTestVersion: (id, versionNumber, expectedCurrentVersion = null) =>
        apiFetch(`/wiki/${id}/restore-test-version`, {
            method: 'POST',
            body: JSON.stringify({
                version_number: versionNumber,
                expected_current_version: expectedCurrentVersion,
                comment: `Restore test config from v${versionNumber}`,
            }),
        }),
    deleteArticle: (id) => apiFetch(`/wiki/${id}`, { method: 'DELETE' }),
    // Confluence/Notion-style «Сделать копию»: backend клонирует контент и тест
    // в новый draft автора без аудитории и без истории прохождений. Видеть и
    // редактировать копию сможет только автор (плюс admin/HR/методолог).
    duplicateArticle: (id) => apiFetch(`/wiki/${id}/duplicate`, { method: 'POST' }),
    search: (q) => apiFetch(`/wiki/search?q=${encodeURIComponent(q)}`),
    // Кабинет сотрудника → вкладка «Обучение» в Inbox.
    // Возвращают только тех wiki-материалов, к которым у current_user есть
    // доступ — фильтр идентичен `GET /wiki/`.
    myFeedback: ({ sinceDays = 60, limit = 20 } = {}) =>
        apiFetch(`/wiki/my-feedback?since_days=${sinceDays}&limit=${limit}&_t=${Date.now()}`),
    myRecommendations: ({ limit = 10 } = {}) =>
        apiFetch(`/wiki/my-recommendations?limit=${limit}&_t=${Date.now()}`),
    sections: (category) => apiFetch(`/wiki/sections/${encodeURIComponent(category)}`),
    reorder: (id, sort_order, section_group) => {
        const qs = new URLSearchParams({ sort_order })
        if (section_group !== undefined) qs.set('section_group', section_group === null ? '' : section_group)
        return apiFetch(`/wiki/${id}/reorder?${qs}`, { method: 'POST' })
    },
    reorderFolder: (folderName, direction, category) => {
        const qs = new URLSearchParams({ folder_name: folderName, direction })
        if (category && category !== 'Все') qs.set('category', category)
        return apiFetch(`/wiki/reorder-folder?${qs}`, { method: 'POST' })
    },

    // ── Per-question test submit (manual review path) ──────────────
    // Ходит сюда только когда хотя бы один вопрос имеет
    // requires_manual_review=true. Auto-graded тесты по-прежнему
    // ходят на legacy /wiki/{id}/test (blob-эндпоинт) без изменений.
    submitTestAnswer: (articleId, payload) => apiFetch(
        `/wiki/${articleId}/test/submit-answer`,
        { method: 'POST', body: JSON.stringify(payload) },
    ),

    // ── Test attachments (wiki author / methodologist / admin) ─────
    uploadTestAttachment: async (file, questionEntityId) => {
        const fd = new FormData()
        fd.append('file', file)
        const qid = questionEntityId ? `&entity_id=${questionEntityId}` : ''
        return apiFetch(
            `/files/upload?entity_type=wiki_question_attachment${qid}`,
            { method: 'POST', body: fd, headers: {} },
        )
    },

    // ── Wiki test review queue (article author/owner + methodologist) ──
    reviewQueue: (params = {}) => {
        const qs = new URLSearchParams(params).toString()
        return apiFetch(`/wiki/review/queue${qs ? `?${qs}` : ''}`)
    },
    reviewItem: (attemptId) => apiFetch(`/wiki/review/queue/${attemptId}`),
    reviewVerdict: (attemptId, data) => apiFetch(
        `/wiki/review/${attemptId}/verdict`,
        { method: 'POST', body: JSON.stringify(data) },
    ),
    reviewStats: () => apiFetch('/wiki/review/stats'),
    dismissAttempt: (attemptId) => apiFetch(`/wiki/review/${attemptId}/dismiss`, { method: 'POST' }),
    // «Дать ещё попытку»: методолог/руководитель снимает блок пересдачи теста
    // статьи (сброс кулдауна + исчерпанных попыток + провального вердикта).
    grantRetake: (employeeId, articleId) => {
        const qs = new URLSearchParams({ employee_id: employeeId, article_id: articleId }).toString()
        return apiFetch(`/wiki/review/grant-retake?${qs}`, { method: 'POST' })
    },

    // ── KB Dept-Head RBAC v2 — Access Grants (PR-2) ────────────────
    // Spec: docs/specs/kb-dept-head-rbac-2026q2/spec.md §4.2-4.4
    listGrants: (articleId) => apiFetch(`/wiki/${articleId}/grants`),
    grantAccess: (articleId, { subject_type, subject_id, expires_at, note }) =>
        apiFetch(`/wiki/${articleId}/grants`, {
            method: 'POST',
            body: JSON.stringify({ subject_type, subject_id, expires_at, note }),
        }),
    revokeGrant: (articleId, grantId) => apiFetch(
        `/wiki/${articleId}/grants/${grantId}`,
        { method: 'DELETE' },
    ),

    // ── KB Change Tracking — Test Drafts (Wave 1.5 PR-4) ──────────
    // Spec: docs/specs/dialogue-trainer-v2-wave1.5-kb-change-tracking/spec.md §7.3
    listTestDrafts: (articleId, { status = 'pending' } = {}) => {
        const qs = new URLSearchParams({ status, _t: Date.now() }).toString()
        return apiFetch(`/wiki/${articleId}/test-drafts?${qs}`)
    },
    approveTestDraft: (articleId, draftId, { edited_questions } = {}) =>
        apiFetch(`/wiki/${articleId}/test-drafts/${draftId}/approve`, {
            method: 'POST',
            body: JSON.stringify({ edited_questions: edited_questions ?? null }),
        }),
    rejectTestDraft: (articleId, draftId, { rejection_reason }) =>
        apiFetch(`/wiki/${articleId}/test-drafts/${draftId}/reject`, {
            method: 'POST',
            body: JSON.stringify({ rejection_reason }),
        }),

    // ── Итоговый тест блока (section final test) ──────────────────
    finalTestBuilder: (sectionGroup) =>
        apiFetch(`/wiki/sections/${encodeURIComponent(sectionGroup)}/final-test/builder?_t=${Date.now()}`),
    saveFinalTest: (sectionGroup, payload) =>
        apiFetch(`/wiki/sections/${encodeURIComponent(sectionGroup)}/final-test`, {
            method: 'PUT',
            body: JSON.stringify(payload),
        }),
    finalTestInfo: (sectionGroup) =>
        apiFetch(`/wiki/sections/${encodeURIComponent(sectionGroup)}/final-test?_t=${Date.now()}`),
    startFinalTest: (sectionGroup) =>
        apiFetch(`/wiki/sections/${encodeURIComponent(sectionGroup)}/final-test/start`, { method: 'POST' }),
    submitFinalTest: (sectionGroup, answers) =>
        apiFetch(`/wiki/sections/${encodeURIComponent(sectionGroup)}/final-test/submit`, {
            method: 'POST',
            body: JSON.stringify({ answers }),
        }),
}
